import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { fetchClient } from '../shared/api/fetching';
import Dropdown from "../shared/components/Dropdown";
import ButtonGroup from "../shared/components/ButtonGroup";
import ToggleButton from "../shared/components/ToggleButton";
import PlayerStatsTable from "./components/PlayerStatsTable";
import StatsSidebar from "../shared/components/StatsSidebar";
import ErrorDisplay from "../shared/components/ErrorDisplay";
import { Filters } from "../shared/svg/Svg";
import {
  getClubsData,
  getForgeCompetitionsSportecId,
  filterTypes, queryStatusTypes, mlsPhaseTypes,
  playerStatsPositionTypes
} from "../shared/utils";
import { actionTypes, useFilters } from "../shared/hooks/useFilters";
import { statTypes } from "../shared/components/Table/utils";
import PlayerStatsData from "./components/PlayerStatsData";
import { useReactAppState } from "../shared/containers/utils";
import { useHash, getHashQueryObject, getStatTypeQuery, getStatTypeFromQuery } from './utils';
import mlsEndpoints from "../shared/api/mlsEndpoints";
import d3endpoints from '../shared/api/d3endpoints';
import { statsTypeMap } from '../shared/api/mlsStatsMap';
import { d3PlayerStatsAPI } from '../shared/api/variables';

function PlayerStats({ options }) {
  const { mediaQueries, t, queryParams, apiList } = useReactAppState();
  const { d3SportsAPI, statsAPI } = apiList || {};
  const { isMediumLargeMin, isXLargeMin, isMediumMin, isLargeMin } = mediaQueries || {};

  const [hashQuery, setHashQuery] = useHash();
  const [hashes, setHashes] = useState([]);

  const { queryType,
    "stat-type": queryStatType,
    weather: queryWeather,
    season: querySeason,
    competitionSportecId: queryCompetitionSportecId,
    competitionSlug: queryCompetitionSlug,
    versus: queryVersus,
    dateRange: queryDateRange,
    position: queryPosition,
    appearances: queryAppearances,
    clubAbbreviation: queryClubAbbreviation,
    clubSportecId: queryClubSportecId,
  } = queryParams || {};

  const activeClubs = options.activeClubs;
  const [hideClubsDropdown] = useState(options.hideClubsDropdown);
  const [hideCompetitionsDropdown] = useState(options.hideCompetitionsDropdown);
  const [hideSeasonsDropdown] = useState(options.hideSeasonsDropdown);
  const [hideFiltersCTA] = useState(options.hideFiltersCTA);
  const [isOpen, setIsOpen] = useState(false);
  const [forgeCompetitions] = useState(getForgeCompetitionsSportecId(options.competitions, false));
  const [currentQueryType, setCurrentQueryType] = useState(queryType?.[0] || options?.queryType || statTypes.statsGeneral);
  const [initialStatType] = useState(currentQueryType === statTypes.statsGeneral || currentQueryType === statTypes.statsAttacking
    ? "goals"
    : currentQueryType === statTypes.statsPassing
      ? "accurate_pass"
      : "goals_conceded");

  const [statTypesArray] = useState([
    {
      statType: statTypes.statsGeneral,
      name: t("stats_general")
    },
    {
      statType: statTypes.statsPassing,
      name: t("stats_passing")
    },
    {
      statType: statTypes.statsAttacking,
      name: t("stats_attacking")
    },
    {
      statType: statTypes.statsDefending,
      name: t("stats_defending")
    },
    {
      statType: statTypes.statsGoalKeeping,
      name: t("stats_goalkeeping")
    }]);

  const initialDesc = queryStatType && queryStatType.length > 0 ? !!(queryStatType?.[0]) : true;

  const { state, initialStateRef, badgeCount, reset: resetFilters, updateFilter } = useFilters({
    initialState: {
      appearances: queryAppearances?.[0] || filterTypes.all,
      badgeCount: 0,
      club: queryClubAbbreviation?.[0] || options?.clubAbbreviation || filterTypes.allClubs,
      clubSportecId: options?.clubSportecId || queryClubSportecId?.[0] || filterTypes.allClubs,
      clubs: [],
      competitionSlug: queryCompetitionSlug?.[0] || options.competitionSlug || forgeCompetitions?.[0]?.slug,
      competitionSportecId: queryCompetitionSportecId?.[0] || options.competitionSportecId || forgeCompetitions?.[0]?.fields?.sportecId || forgeCompetitions?.[0]?.sportecId,
      competitions: forgeCompetitions,
      currentQueryType,
      dateRange: queryDateRange?.[0] || options.dateRange || filterTypes.all,
      desc: initialDesc,
      mlsPhaseType: forgeCompetitions?.[0]?.fields?.mlsPhaseType || forgeCompetitions?.[0]?.mlsPhaseType || mlsPhaseTypes.regular,
      page: 0,
      position: queryPosition?.[0] || options.position || filterTypes.all,
      preSelectedClub: !!(options.clubSportecId),
      route: "seasons",
      season: querySeason?.[0] || options.season,
      sortId: null,
      statType: queryStatType?.[0] || options.statType || initialStatType,
      versus: queryVersus?.[0] || options.versus || filterTypes.all,
      weather: queryWeather?.[0] || options.weather || filterTypes.all,
    }
  });

  const handleAlignment = (event, newAlignment) => {
    if (newAlignment) {
      const resetPosition = newAlignment !== state?.currentQueryType && state?.currentQueryType === statTypes.statsGoalKeeping;
      setCurrentQueryType(newAlignment);
      if (newAlignment === statTypes.statsGoalKeeping) {
        updateFilter(actionTypes.position)(playerStatsPositionTypes.goalkeeper);
      } else if (resetPosition) {
        updateFilter(actionTypes.position)(filterTypes.all);
      }

      updateFilter(actionTypes.currentQueryType)(newAlignment);
    }
  };

  const {
    clubs,
    competitions,
    competitionSlug,
    clubSportecId,
    statType,
    desc,
    page: currentPage,
    position,
    appearances,
    weather,
    dateRange,
    versus,
    competitionSportecId,
    season,
    route,
    preSelectedClub,
    mlsPhaseType
  } = state || {};

  const page = currentPage + 1;

  const seasonsEndpoint = mlsEndpoints.getSeasons.replace("{competition_id}", competitionSportecId);

  const {
    isLoading: isLoadingSeasons,
    isError: isErrorSeasons,
    data: seasons,
    error: seasonsError
  } = useQuery({
    queryKey: ["mls-seasons", competitionSportecId],
    queryFn: () => fetchClient({ endpoint: seasonsEndpoint, apiURL: statsAPI }),
    notifyOnChangeProps: ['data', 'error'],
    enabled: !!competitionSportecId,
  });

  const isAllClubs = clubSportecId === filterTypes.allClubs;


  const currentSeasonId = useMemo(() => {
    const mlsSeason = seasons?.seasons?.find(seasonItem => Number(seasonItem.season) === Number(season));
    return mlsSeason ? mlsSeason.season_id : seasons?.seasons?.[0]?.season_id
  }, [season, seasons])

  const clubStatsEndpoint = mlsEndpoints.getSeasonStatisticsByCompetitionIdAndSeasonId
    .replace("{competition_id}", competitionSportecId)
    .replace("{season_id}", currentSeasonId);

  const playerStatsEndpoint = d3PlayerStatsAPI({
    competitionSportecId,
    seasonSportecId: currentSeasonId,
    sortColumn: statsTypeMap[state?.statType] || state?.statType,
    sortOrder: state?.desc ? "desc" : "asc",
    page,
    pageSize: isAllClubs ? 30 : 1000,
    clubSportecId: clubSportecId?.toLowerCase().includes("all") ? null : clubSportecId
  })

  const { data: clubsData,
    status: clubsStatus,
  } = useQuery({
    queryKey: [`mls-player-stats`, { clubStatsEndpoint }],
    queryFn: () => fetchClient({ endpoint: clubStatsEndpoint, apiURL: statsAPI }),
    select: clubsData => getClubsData(clubsData, activeClubs),
    notifyOnChangeProps: ['data', 'error'],
    enabled: !!clubStatsEndpoint && !preSelectedClub && !!currentSeasonId
  });

  const { data: statsData, isLoading: isLoadingStats, isError: isErrorStats, isFetching: isFetchingStats } = useQuery({
    queryKey: [`mls-player-stats`, {
      page,
      currentQueryType: currentQueryType ? currentQueryType : null,
      route,
      statType: statType ? statType : null,
      isAllClubs,
      competitionSlug,
      competitionSportecId: competitionSportecId ? competitionSportecId : null,
      season: season ? season : null,
      desc: desc !== undefined ? desc : undefined,
      appearances: appearances ? appearances : null,
      position: position ? position : null,
      dateRange: dateRange ? dateRange : null,
      versus: versus ? versus : null,
      weather: weather ? weather : null,
      mlsPhaseType: mlsPhaseType ? mlsPhaseType : null
    }],
    keepPreviousData: true,
    queryFn: () => fetchClient({ endpoint: playerStatsEndpoint, apiURL: d3SportsAPI }),
    enabled: !!playerStatsEndpoint && !!currentSeasonId,
    select: data => {
      return isAllClubs ? data : data?.filter(player => player.team_id === clubSportecId);
    },
    retry: false
  });

  const mappedSeasons = seasons?.seasons?.map((s) => {
    return { label: s.season, value: s.season };
  });

  useEffect(() => {
    if (clubsData != null) {
      updateFilter(actionTypes.clubs)(clubsData);
    }
  }, [clubsData]);

  useEffect(() => {
    if (!options?.disableDeeplinking) {
      const competitionSlugHash = state?.competitionSlug === "all-competitions" ? "all" : state?.competitionSlug;
      const clubSportecIdHash = state?.clubSportecId === filterTypes.allClubs ? "all" : state?.clubSportecId;
      const clubQuery = `club=${clubSportecIdHash}&`;
      const seasonQuery = !hideSeasonsDropdown ? `season=${state?.season}&` : "";
      const competitionQuery = !hideCompetitionsDropdown ? `competition=${competitionSlugHash}&` : "";
      const statTypeQuery = `statType=${getStatTypeQuery(state?.currentQueryType)}&`;
      const positionQuery = `position=${state?.position?.toLowerCase()}&`;
      const fullQuery = `${seasonQuery}${competitionQuery}${clubQuery}${statTypeQuery}${positionQuery}`;

      if (fullQuery) {
        setHashQuery(fullQuery.slice(0, fullQuery.length - 1));
      }
    }
  }, [state]);

  useEffect(() => {
    if (hashQuery && !options?.disableDeeplinking) {
      if (hashes?.includes(hashQuery) || !hashes.length) {
        const hashQueryObject = getHashQueryObject(hashQuery);

        Object.entries(hashQueryObject).forEach(([key, value]) => {
          if (key === "competition" &&
            (state?.competitions?.filter(c => c?.slug === value).length > 0)) {
            updateFilter(actionTypes["competitionSlug"])(value === "all" ? "all-competitions" : value);
          }

          if (key === "club" &&
            (state?.clubs?.filter(c => c?.sportecId?.toString?.() === value).length > 0 || state?.clubs?.length === 0 || value === "all")) {
            updateFilter(actionTypes["clubSportecId"])(value === "all" ? filterTypes.allClubs : value);
          }

          if (key === "season") {
            updateFilter(actionTypes.season)(value);
          }

          if (key === "statType" && value) {
            value = getStatTypeFromQuery(value);
            handleAlignment(null, value);
          }

          if (key === "position" && playerStatsPositionTypes[value]) {
            updateFilter(actionTypes.position)(playerStatsPositionTypes[value]);
          }
        });
      }

      setHashes([...hashes, hashQuery]);
    }
  }, [hashQuery]);


  const updateCompetitionSlug = (competition) => {
    if (state.competitionSlug === competition) {
      return updateFilter(actionTypes.competitionSlug);
    }

    const matchCompetition = state?.competitions.find(c => c.slug === competition);

    const { sportecId, mlsPhaseType } = matchCompetition || {};
    const competitionSportecId = sportecId + "";

    return updateFilter(actionTypes.apply)(
      {
        page: 0,
        competitionSlug: competition,
        competitionSportecId,
        mlsPhaseType,
        clubSportecId: preSelectedClub ? (state?.clubSportecId ? state?.clubSportecId : filterTypes.allClubs) : filterTypes.allClubs
      }
    );
  };


  const isLoadingStatsData = isLoadingStats || isFetchingStats;
  const statsTableData = isLoadingStatsData ? null : isErrorStats ? [] : statsData;

  return (
    <div role="region" className="mls-c-stats">
      <div className="mls-c-stats__header">
        <div className="mls-c-stats__dropdown-group">
          {
            hideSeasonsDropdown
              ? null
              : (
                isLoadingSeasons
                  ? <label className="mls-o-buttons__dropdown-button mls-o-buttons__dropdown-button--label mls-o-buttons__dropdown-button--right ">
                    <span className="mls-o-buttons__dropdown-label">Year</span>
                    <select>
                      <option className="mls-o-buttons__dropdown-item" value="loading">{t("loading")}</option>
                    </select>
                  </label>
                  : isErrorSeasons
                    ? <ErrorDisplay error={seasonsError} />
                    : isMediumMin && seasons
                      ? <Dropdown
                        onChangeFn={updateFilter(actionTypes.season)}
                        items={mappedSeasons}
                        displayKey={"label"}
                        valueKey={"value"}
                        currentValue={season}
                      />
                      : null
              )
          }
          {
            !hideCompetitionsDropdown && isMediumLargeMin && competitions
              ? <Dropdown
                valueKey={"slug"}
                displayKey={"shortName"}
                onChangeFn={updateCompetitionSlug}
                items={competitions}
                currentValue={competitionSlug}
              />
              : null
          }
          {
            hideClubsDropdown
              ? null
              : (
                clubsStatus === queryStatusTypes.loading || clubsStatus === queryStatusTypes.idle
                  ? <label className="mls-o-buttons__dropdown-button mls-o-buttons__dropdown-button--label mls-o-buttons__dropdown-button--right ">
                    <span className="mls-o-buttons__dropdown-label">Club</span>
                    <select>
                      <option className="mls-o-buttons__dropdown-item" value="loading">Loading...</option>
                    </select>
                  </label>
                  : clubsStatus === queryStatusTypes.error
                    ? <ErrorDisplay error={clubsError} />
                    : isXLargeMin && clubs
                      ? <Dropdown onChangeFn={updateFilter(actionTypes.clubSportecId)} displayKey={"team_short_name"} valueKey={"team_id"} items={clubs} currentValue={clubSportecId} />
                      : null
              )
          }
          {
            !hideFiltersCTA
              ? (
                <ToggleButton
                  disabled={isOpen}
                  onClick={() => setIsOpen(!isOpen)}
                  className="mls-o-buttons__icon mls-o-buttons__icon--right" value="filters"
                  aria-label="all filters">
                  {t("all_filters")}
                  <Filters />
                  {
                    badgeCount && badgeCount > 0
                      ? <span className="mls-o-badge">{badgeCount}</span>
                      : null
                  }
                </ToggleButton>
              ) : null}
        </div>
        {
          isLargeMin
            ? (
              <ButtonGroup
                className={'mls-o-buttons__segmented mls-o-buttons__segmented--five desktop'}
                value={currentQueryType}
                onChange={handleAlignment}
                aria-label="statistics category"
              >
                <ToggleButton className="mls-o-buttons__segment" value={statTypes.statsGeneral} aria-label="general">
                  {t("stats_general")}
                </ToggleButton>
                <ToggleButton className="mls-o-buttons__segment" value={statTypes.statsPassing} aria-label="passing">
                  {t("stats_passing")}
                </ToggleButton>

                <ToggleButton className="mls-o-buttons__segment" value={statTypes.statsAttacking}
                  aria-label="attacking">
                  {t("stats_attacking")}
                </ToggleButton>

                <ToggleButton className="mls-o-buttons__segment" value={statTypes.statsDefending}
                  aria-label="defending">
                  {t("stats_defending")}
                </ToggleButton>

                <ToggleButton className="mls-o-buttons__segment" value={statTypes.statsGoalKeeping}
                  aria-label="Goalkeeping">
                  {t("stats_goalkeeping")}
                </ToggleButton>
              </ButtonGroup>
            )
            : (
              <Dropdown
                extraClass={"mobile"}
                onChangeFn={(val) => handleAlignment(null, val)}
                displayKey={"name"}
                valueKey={"statType"}
                items={statTypesArray}
                currentValue={currentQueryType}
              />
            )
        }
      </div>
      <PlayerStatsData
        page={currentPage}
        clubs={clubs}
        clubSportecId={clubSportecId}
        seasons={seasons}
        season={season}
        competitions={competitions}
        currentQueryType={currentQueryType}
        updateFilter={updateFilter}
        resetFilters={resetFilters}
        statType={statType}
        position={position}
        appearances={appearances}
        weather={weather}
        dateRange={dateRange}
        desc={desc}
        versus={versus}
        isOpen={isOpen}
        competitionSlug={competitionSlug}
        competitionSportecId={competitionSportecId}
        statsData={statsTableData}
        mlsPhaseType={mlsPhaseType}
        preSelectedClub={!isAllClubs}
      >
        <div className={`mls-c-stats__wrapper`}>
          <PlayerStatsTable
            baseClass={"mls-c-stats"}
            emptyResultsComponent={
              <ToggleButton
                onClick={() => setIsOpen(!isOpen)}
                className="mls-o-buttons__icon mls-o-buttons__icon--right" value="filters"
                aria-label="update filters">
                {t("update_filters")}
                <Filters />
              </ToggleButton>
            } />
        </div>
        <StatsSidebar
          setIsOpen={setIsOpen}
          initialState={initialStateRef}
          isOpen={isOpen}
          querySelector={'body'}
          title={t("stat_filters")}
          enableAllYears={options?.enableAllYears}
        />
      </PlayerStatsData>
    </div >
  );
}

// eslint-disable-next-line no-func-assign
PlayerStats = React.memo(PlayerStats);

export default PlayerStats;
